import * as _ from 'lodash';
import * as React from 'react';
import { ChangeEvent } from 'react';
import * as moment from 'moment';
import * as qs from 'query-string';
import { Dispatch, connect } from 'react-redux';
import { GridApi, GridReadyEvent, CsvExportParams, ProcessCellForExportParams, ColumnApi } from 'ag-grid-community';
import { IInvoiceDetailReportResponse, IApplicationState, AppActionCreators, ReportActionCreators } from '@nkr/store/index';
import { IInvoiceLineItem, IInvoiceLineItemReport, IInvoiceDetailReport, IInvoiceHeaderDescription, ICenterBase } from '@nkr/models/index';
import { FormControl, Button, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { IInvoiceDetailTotal } from './total';
import { downloadFile } from '../../../services/download-file';
import { OrganType } from '@nkr/enums/organ-type';
import Grid from './grid';

interface IInvoiceDetailGroup {
    id: number,
    name: string,
    group: string,
    fields: string[]
}

const groups: IInvoiceDetailGroup[] = [
    { id: 2, name: 'DASH', group: 'dash', fields: ['dashFee', 'dashFeeLiver'] },
    {
        id: 3, name: 'Transplant', group: 'transplant',
        fields: [
            'transplantFee', 'transplantLiverFee', 'technologySupportFee', 'logisticsFee', 'logisticsFeeLiver', 'shippingFee', 'opoFee', 'researchSupportFee', 'micrositeSupportFee', 'medicaidFee', 'ctFee',
            'phlebotFee', 'crossMatchFee', 'crossMatchKitFee', 'homeBloodDrawFee', 'homeBloodDrawKit', 'remoteRiskPremium', 'incompatiblePairRiskPremium'
        ]
    },
    { id: 4, name: 'Donor Shield', group: 'donorShield', fields: ['donorInsuranceAndSupport', 'donorInsuranceAndSupportLiver', 'donorShieldFee', 'donorShieldLiverFee', 'dppFee'] },
    { id: 5, name: 'Histo', group: 'histo', fields: ['transplantTypingFee', 'typingSwabKit'] },
    { id: 6, name: 'PWL', group: 'pwl', fields: ['pwlQuestFee'] },
    { id: 7, name: 'Serology', group: 'serology', fields: ['serologyFee', 'serologyFeeDirect', 'serologyKitFee'] },
    { id: 8, name: 'CCB', group: 'ctcb', fields: ['streamlinedHospitalBillingFee', 'streamlinedBillingFee', 'donorWorkupFee'] },
    { id: 9, name: 'RMCR', group: 'rmcr', fields: ['nkrFee', 'nkrFee'] },
    { id: 10, name: 'Membership', group: 'membership', fields: ['annualMembershipFee', 'annualDcnFee'] },
    { id: 11, name: 'Misc', group: 'misc', fields: ['miscLineItemTotal'] }
];

const liverVisibleColumns = [
    'rank', 'InvoiceNumber', 'organType', 'center', 'balanceCheck',
    'dashFeeLiver', 'transplantFee', 'logisticsFee',
    'homeBloodDrawKit', 'donorShieldLiverFee', 'donorShieldCreditsLiver',
    'pwlQuestFee', 'serologyFeeDirect', 'serologyKitFee',
    'stateTaxes', 'amountDue'
];

const kidneyHiddenColumns = ['dashFeeLiver', 'donorInsuranceAndSupportLiver', 'donorShieldLiverFee', 'donorShieldCreditsLiver'];

interface IOwnProps { }

interface IStateProps { }

interface IDispatchProps {
    showLoading: (message?: string) => Promise<void>,
    hideLoading: () => void,
    getInvoiceDetailReport: (startDate: string, endDate: string, showAbbrv: boolean, organType: OrganType) => Promise<IInvoiceDetailReportResponse>,
    getInvoiceLineItemsExport: (startDate: string, endDate: string, organType: OrganType) => Promise<any>
}

type TProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    centerId: number,
    organTypes: OrganType[],
    reportData: IInvoiceDetailReport[],
    lineItemData: IInvoiceLineItem[],
    invoiceLineItemReports: IInvoiceLineItemReport[],
    headerDescription: IInvoiceHeaderDescription,
    show: number[],
    gridApi: GridApi,
    columnApi: ColumnApi,
    startDate: string,
    endDate: string,
    hasData: boolean
}

class InvoiceDetailReport extends React.Component<TProps, IState> {
    readonly state: IState = {
        centerId: 0,
        organTypes: [OrganType.Kidney, OrganType.Liver],
        reportData: [],
        lineItemData: [],
        invoiceLineItemReports: [],
        headerDescription: null,
        show: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11],
        gridApi: null,
        columnApi: null,
        startDate: moment().add(-1, 'month').startOf('month').format('YYYY-MM-DD'),
        endDate: moment().add(-1, 'month').endOf('month').format('YYYY-MM-DD'),
        hasData: false
    }

    onGridReady = (e: GridReadyEvent) => {
        this.setState({
            gridApi: e.api,
            columnApi: e.columnApi
        }, () => {
            this.showHideOrganTypeColumns();
        });

        e.api.addEventListener('gridSizeChanged', () => {
            if (this.state.organTypes.length == 1 && this.state.organTypes[0] == OrganType.Liver) {
                e.api.sizeColumnsToFit();
            }
        });
    }

    componentDidMount = () => {
        this.state.hasData = false;
        this.getData();
    }

    getData = () => {
        let q = qs.parse(location.search);
        let showAbbrv = q.showAbbrv != null ? q.showAbbrv : false;
        this.state.hasData = false;
        let organTypes = this.state.organTypes;
        let organType = organTypes.length == 2 ? null : organTypes[0];

        this.props.showLoading('Getting report data, one moment please...').then(() => {
            this.props.getInvoiceDetailReport(this.state.startDate, this.state.endDate, showAbbrv, organType).then((response: IInvoiceDetailReportResponse) => {
                if (response.data.success) {
                    let { invoices, lineItems, invoiceLineItemReports, headerDescription } = response.data.results;

                    this.setState({
                        reportData: invoices,
                        lineItemData: lineItems,
                        invoiceLineItemReports,
                        headerDescription,
                        hasData: true
                    });
                }

                this.props.hideLoading();
            });
        });
    }

    getTotals = () => {
        let totals: IInvoiceDetailTotal = {
            isSummary: true,
            transplantCount: 0,
            amountDue: 0,
            lineItemTotal: 0,
            amountPaid: 0,
            annualMembershipFee: 0,
            annualDcnFee: 0,
            technologySupportFee: 0,
            customLineItemFee: 0,
            nkrFee: 0,
            dashFee: 0,
            dashFeeLiver: 0,
            crossMatchFee: 0,
            crossMatchKitFee: 0,
            crossMatchVibtFee: 0,
            vxmFee: 0,
            serologyFee: 0,
            serologyFeeDirect: 0,
            serologyKitFee: 0,
            logisticsFee: 0,
            logisticsFeeLiver: 0,
            shippingFee: 0,
            transplantFee: 0,
            transplantLiverFee: 0,
            opoFee: 0,
            dppFee: 0,


            patientTypingFee: 0,
            typingFee: 0,
            typingSwabKit: 0,
            typingAndCryoFee: 0,
            typingCryoKit: 0,

            transplantTypingFee: 0,
            remoteRiskPremium: 0,
            incompatiblePairRiskPremium: 0,
            privateInsuranceRiskPremium: 0,
            pwlPhlebotKit: 0,


            researchSupportFee: 0,
            micrositeSupportFee: 0,
            donorInsuranceAndSupport: 0,
            donorInsuranceAndSupportLiver: 0,
            donorShieldCredits: 0,
            donorShieldCreditsLiver: 0,
            ctFee: 0,
            medicaidFee: 0,
            homeBloodDrawFee: 0,
            homeBloodDrawKit: 0,
            covid19Fee: 0,
            phlebotFee: 0,
            donorShieldFee: 0,
            donorShieldLiverFee: 0,
            pwlQuestFee: 0,
            pwlPhlebotFee: 0,
            pwlJugOnlyFee: 0,
            physicianCharge: 0,
            anesthesiaCharge: 0,
            hospitalCharge: 0,
            pwlPerTransplantFee: 0,
            rmcrFeeMinor: 0,
            rmcrFeeMajor: 0,
            streamlinedHospitalBillingFee: 0,
            streamlinedBillingFee: 0,
            donorWorkupFee: 0,
            miscLineItemTotal: 0,
            monthlyInactivityFee: 0,
            lostGpsFee: 0,
            stateTaxes: 0
        };

        this.state.reportData.forEach(invoice => {
            for (const key in totals) {
                totals[key] += invoice[key];
            }
        });

        return { totals };
    }

    handleShowChange = (id: number) => {
        let show = [...this.state.show];
        let exists = show.indexOf(id) >= 0;

        if (exists) {
            show = this.state.show.filter(x => x != id);
        } else {
            show.push(id);
        }

        this.setState({
            show
        }, () => {
            this.showHideGroups();
        });
    }

    handleCenterChange = (e: ChangeEvent<FormControl & HTMLSelectElement>) => {
        let centerId = parseInt(e.currentTarget.value);
        this.setState({
            centerId: centerId
        });
    }

    handleExportQuickBooks = () => {
        let organTypes = this.state.organTypes;
        let organType = organTypes.length == 2 ? null : organTypes[0];
        let organLabel = organType != null ? organType == OrganType.Kidney ? "K" : "L" : "K";

        this.props.showLoading('Exporting CSV file, one moment please...').then(() => {
            this.props.getInvoiceLineItemsExport(this.state.startDate, this.state.endDate, organType).then((response: any) => {
                downloadFile(response.data, `invoice-line-item-export_${organLabel}_${this.state.startDate}-${this.state.endDate}.csv`, 'text/csv');
                this.props.hideLoading();
            });
        });
    }

    handleDateRangeSearch = () => {
        this.state.hasData = false;
        this.getData();
    }

    handleDateChange = (e: React.FormEvent<FormControl & HTMLInputElement>) => {
        let { name, value } = e.currentTarget;

        if (name == 'startDate') {
            this.setState({
                startDate: value
            });
        }

        if (name == 'endDate') {
            this.setState({
                endDate: value
            });
        }
    }

    changeOrganType = (values: OrganType | OrganType[]) => {
        let safeValues = Array.isArray(values) ? values : [values];
        if (safeValues.length == 0) return;
        this.setState({ organTypes: safeValues }, () => {
            this.showHideGroups();
            this.getData();
        });
    }

    handleExport = () => {

        let params: CsvExportParams = {
            columnGroups: true,
            skipPinnedTop: true,
            skipPinnedBottom: false,
            fileName: `invoice-detail-report_${this.state.startDate}-${this.state.endDate}.csv`,
            processCellCallback: (params: ProcessCellForExportParams) => {
                let colDef = params.column.getColDef();
                let data = params.node.data as IInvoiceDetailReport;
                let isPinnedTotal = params.node.rowPinned == 'bottom';

                if (isPinnedTotal) {
                    if (colDef.field == 'invoiceNumber')
                        return '';

                    if (colDef.field == 'centerAlias')
                        return 'Summary >>';
                }

                if (colDef.field == 'organType')
                    return params.value == 0 ? 'Kidney' : params.value == 1 ? 'Liver' : params.value;

                if (colDef.colId == 'transplantFee')
                    return isPinnedTotal
                        ? (data.transplantFee || 0) + (data.transplantLiverFee || 0)
                        : (data?.organType == 1 ? data.transplantLiverFee : data.transplantFee);

                if (colDef.colId == 'logisticsFee')
                    return isPinnedTotal
                        ? (data.logisticsFee || 0) + (data.logisticsFeeLiver || 0)
                        : (data?.organType == 1 ? data.logisticsFeeLiver : data.logisticsFee);

                return params.value;
            }
        };

        this.state.gridApi.exportDataAsCsv(params);
    }

    showHideGroups = () => {
        groups.forEach(item => {
            let show = this.state.show.find(id => id == item.id);

            item.fields.forEach(field => {
                this.state.columnApi.setColumnVisible(field, show != null);
            });
        });

        this.showHideOrganTypeColumns();
    }

    showHideOrganTypeColumns = () => {
        if (this.state.columnApi == null) {
            return;
        }

        let organTypes = this.state.organTypes;
        let organType = organTypes.length == 2 ? null : organTypes[0];

        this.state.columnApi.setColumnWidths(this.state.columnApi.getAllColumns()
            .filter(column => column.getColDef().width != null)
            .map(column => ({ key: column, newWidth: column.getColDef().width })));

        if (organType == null) {
            return;
        }

        if (organType == OrganType.Liver) {
            this.state.columnApi.getAllColumns().forEach(column => {
                let colId = column.getColId();

                if (liverVisibleColumns.indexOf(colId) < 0) {
                    this.state.columnApi.setColumnVisible(colId, false);
                }
            });

            this.state.gridApi.sizeColumnsToFit();

            return;
        }

        kidneyHiddenColumns.forEach(colId => this.state.columnApi.setColumnVisible(colId, false));
    }

    getCenterList = () => {
        let results: ICenterBase[] = [];

        for (let n = 0; n < this.state.reportData.length; n++) {
            let item = this.state.reportData[n];
            let exists = results.find(x => x.id == item.centerId);

            if (exists == null) {
                results.push({
                    id: item.centerId,
                    alias: item.centerAlias,
                    name: '',
                    isMemberCenter: true,
                    isLiverParticipant: false,
                    centerAddresses: []
                });
            }
        }

        return _.orderBy(results, (item) => {
            return item.alias
        }, 'asc');
    }

    getFilteredData = () => {
        let filteredData = this.state.reportData.filter(x => {
            return this.state.centerId == 0 ? true : this.state.centerId == x.centerId;
        });

        return filteredData;
    }

    render = () => {
        const centerList = this.getCenterList();
        const filteredList = this.getFilteredData();
        const { totals } = this.getTotals();
        const organValue = this.state.organTypes.length == 2 ? -1 : this.state.organTypes[0];

        return (
            <>
                <div style={{ fontSize: 28, fontWeight: 500, marginBottom: 20 }}>Invoice Detail Report</div>

                <div className='well well-sm' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 14, flexWrap: 'nowrap', gap: 10 }}>

                    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 10 }}>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                            <span>Start Date:</span>
                            <FormControl
                                type='date'
                                name='startDate'
                                value={this.state.startDate}
                                onChange={this.handleDateChange}
                                style={{ minWidth: 140 }}
                            />
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' }}>
                            <span>End Date:</span>
                            <FormControl
                                type='date'
                                name='endDate'
                                value={this.state.endDate}
                                onChange={this.handleDateChange}
                                style={{ minWidth: 140 }}
                            />
                        </div>

                        <Button bsStyle='primary' onClick={this.handleDateRangeSearch}>Search</Button>

                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', marginLeft: 16 }}>
                            <span>Donor Center:</span>
                            <FormControl
                                componentClass='select'
                                value={this.state.centerId}
                                onChange={this.handleCenterChange}
                                style={{ minWidth: 120 }}
                            >
                                <option value={0}>- ALL -</option>
                                {centerList.map((item, index) =>
                                    <option key={'ctr-' + index} value={item.id}>{item.alias}</option>
                                )}
                            </FormControl>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap', marginLeft: 16 }}>
                            <span>Organ:</span>
                            <ToggleButtonGroup type='radio' name='organs' value={organValue}>
                                <ToggleButton value={-1} onChange={() => this.changeOrganType([OrganType.Kidney, OrganType.Liver])}
                                    className={this.state.organTypes.length == 2 ? 'btn-primary btn-md' : 'btn-md'}>All</ToggleButton>
                                <ToggleButton value={OrganType.Kidney} onChange={() => this.changeOrganType(OrganType.Kidney)}
                                    className={this.state.organTypes.length == 1 && this.state.organTypes.includes(OrganType.Kidney) ? 'btn-primary btn-md' : 'btn-md'}>Kidney</ToggleButton>
                                <ToggleButton value={OrganType.Liver} onChange={() => this.changeOrganType(OrganType.Liver)}
                                    className={this.state.organTypes.length == 1 && this.state.organTypes.includes(OrganType.Liver) ? 'btn-primary btn-md' : 'btn-md'}>Liver</ToggleButton>
                            </ToggleButtonGroup>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, whiteSpace: 'nowrap' }}>
                        <Button bsSize="small" bsStyle="success" onClick={this.handleExportQuickBooks}>Export CSV for QuickBooks</Button>
                        <Button bsSize="small" onClick={this.handleExport}>Export to Excel</Button>
                    </div>
                </div>
                {this.state.headerDescription && this.state.hasData == true && (
                    <Grid
                        key={this.state.headerDescription.logisticsSupportFeeDescr}
                        data={filteredList}
                        invoiceLineItems={this.state.invoiceLineItemReports}
                        headerDescription={this.state.headerDescription}
                        totals={totals}
                        onGridReady={this.onGridReady} />
                )
                }
            </>
        );
    }
}

const mapDispatchToProps = (dispatch: Dispatch<IApplicationState>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            dispatch(AppActionCreators.hideLoading());
        },
        getInvoiceDetailReport: (startDate, endDate, showAbbrv, organType) => {
            return dispatch(ReportActionCreators.getInvoiceDetailReport(startDate, endDate, showAbbrv, organType));
        },
        getInvoiceLineItemsExport: (startDate, endDate, organType) => {
            return dispatch(ReportActionCreators.getInvoiceLineItemsExport(startDate, endDate, organType));
        },
    };
}

export default connect<IStateProps, IDispatchProps, IOwnProps>(
    null,
    mapDispatchToProps)(InvoiceDetailReport);
